import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Sun, Cloud, Droplets, Sparkles } from 'lucide-react';
import ProductCard from '@/components/ProductCard';
import ProductDetailDialog from '@/components/ProductDetailDialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

export default function WeatherWidget({ region }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openProduct, setOpenProduct] = useState(null);

  useEffect(() => {
    setLoading(true);
    const params = region ? { region } : {};
    api
      .get('/recommendations/weather', { params })
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, [region]);

  if (loading) return <Skeleton className="h-40 rounded-2xl" />;
  if (!data || !data.weather?.available) return null;

  const w = data.weather;
  return (
    <Card className="rounded-2xl bg-card border-border/70 overflow-hidden" data-testid="weather-widget">
      <div className="p-5 sm:p-6 grid md:grid-cols-3 gap-4">
        <div className="md:col-span-1 flex items-start gap-3">
          <div className="h-12 w-12 rounded-full bg-accent/40 border border-rose-tint flex items-center justify-center shrink-0">
            <Sun className="h-6 w-6 text-rose-gold" />
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              Today in {w.city || 'your region'}
            </p>
            <p className="font-display text-2xl text-ink leading-tight">
              {Math.round(w.temp_c)}°C
            </p>
            <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Droplets className="h-3 w-3" /> {w.humidity}%
              </span>
              <span>UV {Math.round(w.uv_index)}</span>
            </div>
          </div>
        </div>
        <div className="md:col-span-2 flex flex-col justify-between">
          <div>
            <span className="text-[11px] uppercase tracking-[0.18em] text-rose-gold flex items-center gap-1.5">
              <Sparkles className="h-3 w-3" /> Weather-aware skin advice
            </span>
            <p className="mt-1.5 text-sm leading-relaxed text-foreground/85">{w.advice}</p>
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {(w.tags || []).slice(0, 5).map((t) => (
              <span
                key={t}
                className="text-[10px] uppercase tracking-[0.14em] text-rose-gold border border-rose-tint bg-accent/40 rounded-full px-2 py-0.5"
              >
                {t.replace('-', ' ')}
              </span>
            ))}
          </div>
        </div>
      </div>

      {(data.products || []).length > 0 && (
        <div className="border-t border-border/70 px-5 sm:px-6 py-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              Picks for today’s weather
            </p>
            <Button asChild variant="ghost" size="sm" className="rounded-xl text-xs">
              <Link to="/products">See all</Link>
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.products.slice(0, 3).map((p) => (
              <ProductCard key={p.id} product={p} onOpen={setOpenProduct} />
            ))}
          </div>
        </div>
      )}

      <ProductDetailDialog
        product={openProduct}
        open={!!openProduct}
        onOpenChange={(o) => !o && setOpenProduct(null)}
      />
    </Card>
  );
}
